import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  Injector,
  input,
  output,
  signal,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule} from '@angular/forms';
import {TextInputIcon} from '../text-input/text-input.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ValidationErrorsDictionary} from '../../../../core/dictionary/validation-errors.dictionary';
import {CommonModule} from '@angular/common';
import {IftaLabelModule} from 'primeng/iftalabel';
import {InputIconModule} from 'primeng/inputicon';
import {InputNumberModule} from 'primeng/inputnumber';
import {IconFieldModule} from 'primeng/iconfield';
import {LabelMode} from '../date-input/date-input.component';
import {FloatLabelModule} from 'primeng/floatlabel';

@Component({
  selector: 'app-numerical-input',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputNumberModule,
    FloatLabelModule,
    IftaLabelModule,
    IconFieldModule,
    InputIconModule,
  ],
  templateUrl: './numerical-input.component.html',
  styleUrl: './numerical-input.component.css',
  encapsulation: ViewEncapsulation.None,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AppNumberInputComponent),
    multi: true,
  }],
})
export class AppNumberInputComponent implements ControlValueAccessor, AfterViewInit {
  private ngControl: NgControl | null = null;

  readonly inputId = input(`number-${crypto.randomUUID().slice(0, 8)}`);
  readonly label = input('');
  readonly labelMode = input<LabelMode>('float');
  readonly helpText = input('');
  readonly filled = input(false);
  readonly fluid = input(true);
  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly showErrors = input(true);
  readonly name = input('');
  readonly pattern = input<string | undefined>(undefined);
  readonly minLength = input<number | undefined>(undefined);
  readonly maxLength = input<number | undefined>(undefined);
  readonly styleClass = input<string | undefined>(undefined);
  readonly min = input<number | null>(null);
  readonly max = input<number | null>(null);
  readonly step = input<number>(1);
  readonly mode = input<'decimal' | 'currency'>('decimal');
  readonly prefix = input<string | undefined>(undefined);
  readonly suffix = input<string | undefined>(undefined);
  readonly useGrouping = input(true);
  readonly showButtons = input(false);
  readonly icon = input<TextInputIcon | null>(null);
  readonly currency = input<string>('PLN');
  readonly currencyDisplay = input<'symbol' | 'code'>('symbol');
  readonly showClearButton = input(false);
  readonly autoFocus = input(false);

  readonly valueChanged = output<number | null>();
  readonly focused = output<Event>();
  readonly blurred = output<Event>();
  readonly iconClicked = output<void>();

  readonly value = signal<number | null>(null);
  readonly isDisabled = signal(false);
  readonly controlState = signal({
    invalid: false,
    touched: false,
    dirty: false,
    errors: null as any,
  });

  protected readonly computedDisabled = computed(() =>
    this.disabled() || this.isDisabled()
  );

  protected readonly hasLabel = computed(() =>
    !!this.label() && this.labelMode() !== 'none'
  );

  protected readonly hasIcon = computed(() => !!this.icon());

  protected readonly hasRequiredRule = computed(() =>
    this.controlState().errors?.['required'] !== undefined
  );

  protected readonly isInvalid = computed(() => {
    const s = this.controlState();
    return s.invalid && (s.touched || s.dirty);
  });

  protected readonly errorMessage = computed(() =>
    ValidationErrorsDictionary.resolveValidationErrors(
      this.controlState().errors
    )
  );

  private onChange: (v: number | null) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  constructor(
    private readonly injector: Injector,
    private readonly destroyRef: DestroyRef) {
  }

  writeValue(value: number | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  ngAfterViewInit(): void {
    this.ngControl = this.injector.get(NgControl, null, {self: true, optional: true});

    this.ngControl?.control?.statusChanges
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateControlState());

    this.updateControlState();
  }

  private updateControlState(): void {
    const c = this.ngControl?.control;
    if (!c) return;

    this.controlState.set({
      invalid: c.invalid,
      touched: c.touched,
      dirty: c.dirty,
      errors: c.errors,
    });
  }

  protected onInput(event: any): void {
    const value = event.value ?? null;
    this.value.set(value);
    this.onChange(value);
    this.valueChanged.emit(value);
  }

  protected onFocus(event: Event): void {
    this.focused.emit(event);
  }

  protected onBlur(event: Event): void {
    this.onTouched();
    this.blurred.emit(event);
    this.updateControlState();
  }

  protected onIconClick(): void {
    if (this.icon()?.clickable) {
      this.iconClicked.emit();
    }
  }
}
